import { ref, watch } from 'vue'

// Settings → «Внешний вид»: accent color, font size and conversation density (the color theme is
// @nuxtjs/color-mode's own `preference`). Kept on this device and applied to <html> — the accent as
// `data-accent` (style.css swaps --primary/--ring for it, per light/dark), the font size as the root
// font size (Tailwind sizes are rem, so text and spacing scale together), the density as
// `data-density` (style.css spaces the conversation out or packs it tighter).

export type AccentColor = 'neutral' | 'blue' | 'violet' | 'green' | 'orange' | 'red' | 'pink'
export type FontSizeStep = 0 | 1 | 2 | 3 | 4
export type ConversationDensity = 'compact' | 'default' | 'comfy' | 'spacious'

export const ACCENT_COLORS: { id: AccentColor, label: string, swatch: string }[] = [
  { id: 'neutral', label: 'Нейтральный', swatch: 'oklch(0.3 0 0)' },
  { id: 'blue', label: 'Синий', swatch: 'oklch(0.58 0.2 258)' },
  { id: 'violet', label: 'Фиолетовый', swatch: 'oklch(0.56 0.22 293)' },
  { id: 'green', label: 'Зелёный', swatch: 'oklch(0.6 0.16 150)' },
  { id: 'orange', label: 'Оранжевый', swatch: 'oklch(0.68 0.18 50)' },
  { id: 'red', label: 'Красный', swatch: 'oklch(0.6 0.22 25)' },
  { id: 'pink', label: 'Розовый', swatch: 'oklch(0.63 0.22 355)' },
]

/** Root font size per step; 2 is the browser default (16px). */
export const FONT_SIZES_PX: Record<FontSizeStep, number> = { 0: 14, 1: 15, 2: 16, 3: 17, 4: 18 }

export const DENSITIES: { id: ConversationDensity, label: string }[] = [
  { id: 'compact', label: 'Компактно' },
  { id: 'default', label: 'Обычно' },
  { id: 'comfy', label: 'Свободно' },
  { id: 'spacious', label: 'Просторно' },
]

export interface AppearanceSettings {
  accent: AccentColor
  fontSize: FontSizeStep
  density: ConversationDensity
}

const STORAGE_KEY = 'appearance'
const DEFAULTS: AppearanceSettings = { accent: 'neutral', fontSize: 2, density: 'default' }

function read(): AppearanceSettings {
  try {
    const raw = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') as Partial<AppearanceSettings>
    return {
      accent: ACCENT_COLORS.some((a) => a.id === raw.accent) ? raw.accent! : DEFAULTS.accent,
      fontSize: Number.isInteger(raw.fontSize) && (raw.fontSize as number) in FONT_SIZES_PX ? raw.fontSize as FontSizeStep : DEFAULTS.fontSize,
      density: DENSITIES.some((d) => d.id === raw.density) ? raw.density! : DEFAULTS.density,
    }
  } catch {
    return { ...DEFAULTS }
  }
}

export const appearance = ref<AppearanceSettings>(typeof window !== 'undefined' ? read() : { ...DEFAULTS })

export function applyAppearance(settings: AppearanceSettings = appearance.value) {
  const root = document.documentElement
  if (settings.accent === 'neutral') delete root.dataset.accent
  else root.dataset.accent = settings.accent
  if (settings.density === 'default') delete root.dataset.density
  else root.dataset.density = settings.density
  root.style.fontSize = settings.fontSize === 2 ? '' : `${FONT_SIZES_PX[settings.fontSize]}px`
}

export function setAppearance(patch: Partial<AppearanceSettings>) {
  appearance.value = { ...appearance.value, ...patch }
}

let installed = false
/** Applies the saved appearance and keeps <html> and storage in sync with later changes. */
export function installAppearance() {
  if (installed || typeof window === 'undefined') return
  installed = true
  applyAppearance()
  watch(appearance, (settings) => {
    applyAppearance(settings)
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(settings))
    } catch {
      // not persisted — still applied for this session
    }
  }, { deep: true })
}
